import { useNavigate } from 'react-router';
import { getMymeParticipation } from '@myme/logic';
import { Blob, MymeParticipation } from '@myme/utils';
import { useTranslation } from 'src/hooks';
import {
  mymeDetailsBlobContentPageRoute,
  mymeDetailsFinishPageRoute,
  mymeDetailsPageRoute
} from 'src/routes/route-list';
import { toast, ToastType } from 'src/utils';
import { useDialog } from 'src/components/organisms';
import { MymeDisplayMode } from './types';

export const useBlobDetailsActions = (
  mymeId: string | undefined,
  setCurrentBlob: React.Dispatch<React.SetStateAction<Blob | null>>,
  setCurrentModuleId: React.Dispatch<React.SetStateAction<string>>,
  setMode: React.Dispatch<React.SetStateAction<MymeDisplayMode>>,
  currentBlob: Blob | null,
  mymeModules: MymeParticipation | undefined
) => {
  const navigate = useNavigate();
  const timeBasedWaitingDialog = useDialog();
  const { t } = useTranslation();

  const openTimeBasedWaitingDialog = async () => {
    if (mymeId) {
      const respone = await getMymeParticipation(mymeId);
      const module = respone.data?.modules?.find(item => item.blobs.find(blob => !blob.progress.isFinished));
      const blob = module?.blobs.find(item => !item.progress.isFinished);

      if (blob && module) {
        setCurrentBlob(blob);
        setCurrentModuleId(module.id);
        if (blob.isActive) {
          navigate(mymeDetailsBlobContentPageRoute(blob.id, mymeId), { state: blob });
          return;
        }
        await timeBasedWaitingDialog.openDialog();
        return;
      }

      toast(ToastType.ERROR, t('pages.mymes.details.no_blob'));
    }
  };

  const handleGoBackToMymeDialog = () => {
    timeBasedWaitingDialog.closeDialog();
    navigate(mymeDetailsPageRoute(mymeId!));
  };

  const startMyme = () => {
    if (mymeModules?.type === 'TIME_BASED' && !currentBlob?.isActive) {
      openTimeBasedWaitingDialog();
      return;
    }
    if (currentBlob && mymeId) {
      navigate(mymeDetailsBlobContentPageRoute(currentBlob.id, mymeId), { state: currentBlob });
      return;
    }
    toast(ToastType.ERROR, t('pages.mymes.details.no_blob'));
  };

  const goToNextBlob = async (selectedBlob?: Blob, selectedModuleId?: string) => {
    if (mymeModules?.type === 'TIME_BASED' && !currentBlob?.isActive) {
      openTimeBasedWaitingDialog();
      return;
    }

    if (selectedBlob && selectedModuleId && mymeId) {
      setCurrentModuleId(selectedModuleId);
      setCurrentBlob(selectedBlob);
      navigate(mymeDetailsBlobContentPageRoute(selectedBlob.id, mymeId), { state: selectedBlob });
      return;
    }

    const response = await getMymeParticipation(mymeId!);
    const newModules = response.data.modules;
    const allBlobs = newModules.flatMap(module => module.blobs);
    const nextBlob = allBlobs.find(blob => !blob.progress.isFinished);
    const nextBlobModule = newModules.find(item => item.blobs.find(blob => !blob.progress.isFinished));

    if (nextBlobModule?.id && nextBlob && mymeId) {
      if (mymeModules?.type === 'TIME_BASED' && !nextBlob?.isActive) {
        openTimeBasedWaitingDialog();
        return;
      }
      setCurrentModuleId(nextBlobModule.id);
      setCurrentBlob(nextBlob);
      navigate(mymeDetailsBlobContentPageRoute(nextBlob.id, mymeId), { state: nextBlob });
      return;
    }
    if (mymeId) {
      navigate(mymeDetailsFinishPageRoute(mymeId));
    }
    setMode('finish');
    setCurrentBlob(null);
  };

  return { timeBasedWaitingDialog, openTimeBasedWaitingDialog, handleGoBackToMymeDialog, startMyme, goToNextBlob };
};
